import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { storage } from '@/lib/storage';
import { handleApiError } from './errors/error-handler';
import { getCurrentLanguage } from '@/utils/language-helper';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.DEV ? '/api' : 'https://maroon-grouse-638711.hostingersite.com');

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds for better reliability
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request Interceptor - Add Authorization token and language parameter
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = storage.getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData - let axios set Content-Type automatically with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // Add language parameter to all requests except admin/public content management
    const currentLanguage = getCurrentLanguage();
    const isAdminContentRequest = config.url?.includes('/admin/content');
    const isPublicContentRequest = config.url?.includes('/public/content');
    
    if (!isAdminContentRequest && !isPublicContentRequest) {
      if (config.params) {
        config.params.lang = currentLanguage;
      } else {
        config.params = { lang: currentLanguage };
      }
    }

    // Debug: Log the request URL with language parameter (remove in production)
    if (import.meta.env.DEV) {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}?lang=${currentLanguage}`);
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(handleApiError(error));
  }
);

// Response Interceptor - Handle errors and account status updates
apiClient.interceptors.response.use(
  (response) => {
    // Check if response contains account status data and update context
    const data = response.data;
    if (data && typeof data === 'object') {
      // Check for account status in different possible locations
      let accountStatusData = null;
      
      // Check in data.data (for object responses)
      if (data.data && typeof data.data === 'object' && 'account_status' in data.data) {
        accountStatusData = {
          account_status: data.data.account_status,
          account_status_label: data.data.account_status_label || '',
          account_reason: data.data.account_reason,
          account_status_updated_at: data.data.account_status_updated_at,
          is_read_only: data.data.is_read_only ?? false,
          can_view_data: data.data.can_view_data ?? true,
        };
      }
      // Check in data.account_status (for array responses)
      else if ('account_status' in data) {
        accountStatusData = {
          account_status: data.account_status,
          account_status_label: data.account_status_label || '',
          account_reason: data.account_reason,
          account_status_updated_at: data.account_status_updated_at,
          is_read_only: data.is_read_only ?? false,
          can_view_data: data.can_view_data ?? true,
        };
      }
      
      if (accountStatusData) {
        window.dispatchEvent(new CustomEvent('accountStatusUpdate', { 
          detail: accountStatusData 
        }));
      }
    }
    
    return response;
  },
  (error: AxiosError) => {
    const apiError = handleApiError(error);

    // Handle 401 Unauthorized - Redirect to login
    if (apiError.status === 401) {
      storage.clearAuth();
      // Only redirect if not already on auth page
      if (window.location.pathname !== '/auth') {
        window.location.href = '/auth';
      }
    }

    return Promise.reject(apiError);
  }
);

export default apiClient;
